import { phoneNumberZodSchema } from "@@/shared/phone";
import type { Except } from "type-fest";
import { z } from "zod";
import { languageCodeZodSchema } from "../translations/language-code";
import { translatableZodSchema } from "../translations/translation";
import {
    payRequestRecipientIdFactory,
    payRequestRecipientIdZodSchema,
} from "./pay-request-recipient-id";

const _payRequestSmsRecipientZodObject = z.object({
    _id: payRequestRecipientIdZodSchema,
    _type: z.literal("PAY_REQUEST_RECIPIENT"),
    type: z.literal("SMS"),
    name: translatableZodSchema.optional(),
    phoneNumber: phoneNumberZodSchema,
    language: languageCodeZodSchema,
});
type PayRequestSmsRecipient = z.infer<typeof _payRequestSmsRecipientZodObject>;
const payRequestSmsRecipientZodSchema: z.ZodSchema<PayRequestSmsRecipient> =
    _payRequestSmsRecipientZodObject;

const _payRequestEmailRecipientZodObject = z.object({
    _id: payRequestRecipientIdZodSchema,
    _type: z.literal("PAY_REQUEST_RECIPIENT"),
    type: z.literal("EMAIL"),
    name: translatableZodSchema.optional(),
    emailAddress: z.string().email(),
    language: languageCodeZodSchema,
});
type PayRequestEmailRecipient = z.infer<
    typeof _payRequestEmailRecipientZodObject
>;
const payRequestEmailRecipientZodSchema: z.ZodSchema<PayRequestEmailRecipient> =
    _payRequestEmailRecipientZodObject;

const payRequestRecipientZodDiscriminatedUnion = z.discriminatedUnion("type", [
    _payRequestSmsRecipientZodObject,
    _payRequestEmailRecipientZodObject,
]);

type PayRequestRecipient = z.infer<
    typeof payRequestRecipientZodDiscriminatedUnion
>;
const payRequestRecipientZodSchema: z.ZodSchema<PayRequestRecipient> =
    payRequestRecipientZodDiscriminatedUnion;

type PayRequestRecipientFactoryParams = Except<
    PayRequestRecipient,
    "_id" | "_type"
>;
const payRequestRecipientFactory = (
    params: PayRequestRecipientFactoryParams,
): PayRequestRecipient => {
    return {
        _id: payRequestRecipientIdFactory(),
        _type: "PAY_REQUEST_RECIPIENT",
        ...params,
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPayRequestRecipient = (item: any): item is PayRequestRecipient => {
    return item?._type === "PAY_REQUEST_RECIPIENT";
};
const isPayRequestSmsRecipient = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
): item is PayRequestSmsRecipient => {
    return isPayRequestRecipient(item) && item.type === "SMS";
};
const isPayRequestEmailRecipient = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
): item is PayRequestEmailRecipient => {
    return isPayRequestRecipient(item) && item.type === "EMAIL";
};

export {
    isPayRequestEmailRecipient,
    isPayRequestRecipient,
    isPayRequestSmsRecipient,
    payRequestEmailRecipientZodSchema,
    payRequestRecipientFactory,
    payRequestRecipientZodSchema,
    payRequestSmsRecipientZodSchema,
};
export type {
    PayRequestEmailRecipient,
    PayRequestRecipient,
    PayRequestSmsRecipient,
};
